import React, { useState, useRef, useEffect } from "react"
import { useSelector } from "react-redux"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { FiSend, FiUser, FiTrash2 } from "react-icons/fi"
import { RiRobot2Line, RiSparklingLine } from "react-icons/ri"
import { GoogleGenerativeAI } from "@google/generative-ai"

/* ── Gemini client (singleton outside component) ──────────────── */
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY)

const SYSTEM_INSTRUCTION = `You are the KodeVedic AI Learning Assistant — a friendly, expert tutor built into the KodeVedic e-learning platform.

About KodeVedic:
- An online education platform focused on programming, web development, data science, and technology
- Students enroll in video-based courses, track progress, and earn certificates
- Instructors create and publish courses; admins manage the platform

Your behaviour:
- Be warm, encouraging, and patient — students may be complete beginners
- Explain concepts clearly with real-world analogies when helpful
- Always include working code examples for technical questions; wrap them in proper markdown code blocks with language tags
- When a student seems stuck, offer hints before full solutions
- If asked about a specific course topic, teach it thoroughly but concisely
- Answer platform questions (how to enroll, how to view progress, etc.)
- Stay focused on education and technology; politely redirect off-topic requests
- Keep responses well-structured using markdown headings, bullet points, and code blocks
- End responses with a short follow-up question or offer to dig deeper when appropriate`

const SUGGESTED_QUESTIONS = [
  "Explain how JavaScript closures work with examples",
  "What is the difference between SQL and NoSQL databases?",
  "How do I start learning React from scratch?",
  "Explain REST API vs GraphQL",
]

/* ── Markdown renderer components ─────────────────────────────── */
const mdComponents = {
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-yellow-50 underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
  code: ({ inline, className, children }) =>
    inline ? (
      <code className="rounded bg-richblack-800 px-1.5 py-0.5 font-mono text-xs text-caribbeangreen-300">
        {children}
      </code>
    ) : (
      <pre className="my-2 overflow-x-auto rounded-lg bg-richblack-900 p-4 font-mono text-xs leading-relaxed text-richblack-100">
        <code>{children}</code>
      </pre>
    ),
  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  h1: ({ children }) => <h1 className="mb-2 text-base font-bold text-richblack-5">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-1.5 text-sm font-bold text-richblack-5">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1 text-sm font-semibold text-richblack-100">{children}</h3>,
  ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-richblack-5">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-yellow-50 pl-3 text-richblack-300 italic">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-richblack-600">
      <table className="w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-richblack-600 text-richblack-5">{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-richblack-700">{children}</tbody>
  ),
  tr: ({ children }) => <tr className="divide-x divide-richblack-700">{children}</tr>,
  th: ({ children }) => (
    <th className="px-3 py-2 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-richblack-200">{children}</td>
  ),
}

/* ── Typing dots indicator ─────────────────────────────────────── */
function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-0.5">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="h-2 w-2 rounded-full bg-richblack-400 animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  )
}

/* ── Helpers ──────────────────────────────────────────────────── */
const makeGreeting = (name) => ({
  role: "assistant",
  content: `Hi **${name || "there"}**! 👋 I'm your KodeVedic AI Learning Assistant.\n\nI can help you with:\n- 📚 **Course concepts** — explain topics from any of your courses\n- 💻 **Code help** — debug, review, or explain code\n- 🚀 **Learning guidance** — what to learn next, how to approach a topic\n- ❓ **Platform questions** — how to use KodeVedic features\n\nWhat would you like to explore today?`,
})

const storageKey = (uid) => `kodevedic_chat_${uid || "guest"}`

/** Convert our messages array → Gemini history format.
 *  Skips the initial greeting (index 0) and ensures the history
 *  ends on a "model" turn (Gemini's requirement). */
const toGeminiHistory = (msgs) => {
  const history = msgs.slice(1).map((m) => ({
    role: m.role === "user" ? "user" : "model",
    parts: [{ text: m.content }],
  }))
  // Drop a trailing user turn if the session ended mid-flight
  while (history.length && history[history.length - 1].role === "user") {
    history.pop()
  }
  return history
}

const initSession = (history) => {
  const model = genAI.getGenerativeModel({
    model: "gemini-2.5-flash-lite",
    systemInstruction: SYSTEM_INSTRUCTION,
  })
  return model.startChat({
    history,
    generationConfig: { maxOutputTokens: 2048, temperature: 0.75 },
  })
}

/* ── Main component ───────────────────────────────────────────── */
export default function Ask() {
  const { user } = useSelector((state) => state.profile)
  const firstName = user?.firstName || "there"
  const key = storageKey(user?._id)

  /* Load synchronously in the lazy initializer so the FIRST render
     already has the saved messages. This prevents the save effect
     from overwriting localStorage with just the greeting on mount. */
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey(user?._id))
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed) && parsed.length > 0) return parsed
      }
    } catch { /* corrupted — fall through */ }
    return [makeGreeting(firstName)]
  })

  const [prompt, setPrompt] = useState("")
  const [loading, setLoading] = useState(false)
  const chatSessionRef = useRef(null)
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)

  /* ── Init Gemini with the already-loaded history ──────────── */
  useEffect(() => {
    chatSessionRef.current = initSession(toGeminiHistory(messages))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // runs once — messages ref is stable on mount

  /* ── Persist every message change to localStorage ────────── */
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(messages.slice(-200)))
    } catch { /* storage full — ignore */ }
  }, [messages, key])

  /* ── Auto-scroll ──────────────────────────────────────────── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, loading])

  /* ── Auto-resize textarea ─────────────────────────────────── */
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 120) + "px"
  }, [prompt])

  /* ── Send message ─────────────────────────────────────────── */
  const sendMessage = async (text) => {
    const trimmed = (text ?? prompt).trim()
    if (!trimmed || loading) return

    setMessages((prev) => [...prev, { role: "user", content: trimmed }])
    setPrompt("")
    setLoading(true)

    try {
      const result = await chatSessionRef.current.sendMessage(trimmed)
      const reply = result.response.text()
      setMessages((prev) => [...prev, { role: "assistant", content: reply }])
    } catch (err) {
      console.error("Gemini error:", err)
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I'm having trouble connecting right now. Please check your connection and try again.",
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const clearChat = () => {
    const fresh = makeGreeting(firstName)
    setMessages([fresh])
    localStorage.removeItem(key)
    chatSessionRef.current = initSession([])
  }

  const showSuggestions = messages.length === 1

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] max-w-4xl flex-col pb-2">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-shrink-0 items-center gap-3 border-b border-richblack-700 pb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-50 text-richblack-900 shadow-sm">
          <RiRobot2Line size={22} />
        </div>
        <div>
          <h1 className="text-base font-bold text-richblack-5">KodeVedic AI Assistant</h1>
          <p className="flex items-center gap-1 text-[11px] text-richblack-400">
            <RiSparklingLine size={11} />
            Your chats are private, stored locally, and never shared with KodeVedic. Use the "Clear" button to delete them anytime.          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-caribbeangreen-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-caribbeangreen-300" />
            Online
          </span>
          <button
            onClick={clearChat}
            title="Clear chat"
            className="flex items-center gap-1.5 rounded-lg border border-richblack-600 px-2.5 py-1.5 text-xs text-richblack-400 transition-colors hover:border-richblack-500 hover:text-richblack-100"
          >
            <FiTrash2 size={12} />
            Clear
          </button>
        </div>
      </div>

      {/* ── Messages ───────────────────────────────────────────── */}
      <div className="flex-1 space-y-5 overflow-y-auto pr-1">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
          >
            {/* Avatar */}
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${msg.role === "user"
                  ? "bg-yellow-50 text-richblack-900"
                  : "bg-richblack-600 text-richblack-5"
                }`}
            >
              {msg.role === "user" ? (
                user?.image ? (
                  <img
                    src={user.image}
                    alt={firstName}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <FiUser size={14} />
                )
              ) : (
                <RiRobot2Line size={16} />
              )}
            </div>

            {/* Bubble */}
            <div
              className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm ${msg.role === "user"
                  ? "rounded-tr-sm bg-yellow-50 text-richblack-900"
                  : "rounded-tl-sm bg-richblack-700 text-richblack-100"
                }`}
            >
              {msg.role === "assistant" ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{msg.content}</ReactMarkdown>
              ) : (
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {loading && (
          <div className="flex gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-richblack-600 text-richblack-5">
              <RiRobot2Line size={16} />
            </div>
            <div className="rounded-2xl rounded-tl-sm bg-richblack-700 px-4 py-3">
              <TypingDots />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Suggested questions (shown only at start) ──────────── */}
      {showSuggestions && (
        <div className="my-3 flex flex-shrink-0 flex-wrap gap-2">
          {SUGGESTED_QUESTIONS.map((q, i) => (
            <button
              key={i}
              onClick={() => sendMessage(q)}
              className="rounded-full border border-richblack-600 bg-richblack-800 px-3 py-1.5 text-xs text-richblack-300 transition-all hover:border-yellow-50/40 hover:text-richblack-100"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* ── Input bar ──────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-t border-richblack-700 pt-4">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            rows={1}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything… (Enter to send, Shift+Enter for new line)"
            className="flex-1 resize-none overflow-hidden rounded-xl border border-richblack-600 bg-richblack-700 px-4 py-3 text-sm text-richblack-5 placeholder:text-richblack-500 transition-colors focus:border-yellow-50/50 focus:outline-none"
          />
          <button
            onClick={() => sendMessage()}
            disabled={!prompt.trim() || loading}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-yellow-50 text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <FiSend size={16} />
          </button>
        </div>
        <p className="mt-2 text-center text-[11px] text-richblack-600">
          KodeVedic AI can make mistakes — verify important information.
        </p>
      </div>
    </div>
  )
}
